import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const KIND_LABELS = {
  weekly_deal: "Offerta Settimanale",
  new_brand: "Nuovo Brand",
  new_pricelist: "Nuovo Listino",
  new_conditions: "Nuove Condizioni",
  new_outlet_product: "Nuovo Prodotto Outlet",
};

// Pick `count` distinct random items from an array.
function pickRandom(arr, count) {
  if (!Array.isArray(arr) || arr.length === 0) return [];
  if (arr.length <= count) return [...arr];
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a.slice(0, count);
}

function bannerImgSrc(b) {
  if (b.image_id) {
    return `${process.env.REACT_APP_BACKEND_URL}/api/banners/images/${b.image_id}`;
  }
  return b.image_url;
}

/**
 * Display `count` (default 2) banners rotating randomly every `intervalMs`.
 * `variant` switches styling between dashboard ("dark overlay") and landing ("inline").
 */
export default function RotatingBanners({
  banners,
  count = 2,
  intervalMs = 60_000,
  variant = "dashboard",
  className = "",
  testid = "rotating-banners",
}) {
  const [picks, setPicks] = useState(() => pickRandom(banners || [], count));

  useEffect(() => {
    setPicks(pickRandom(banners || [], count));
  }, [banners, count]);

  useEffect(() => {
    if (!banners || banners.length <= count) return undefined;
    const id = setInterval(() => {
      setPicks((prev) => {
        // Try to avoid showing the exact same pair twice in a row.
        const prevIds = new Set(prev.map((b) => b.id));
        let next = pickRandom(banners, count);
        let attempts = 0;
        while (
          attempts < 5 &&
          next.length === prev.length &&
          next.every((b) => prevIds.has(b.id))
        ) {
          next = pickRandom(banners, count);
          attempts += 1;
        }
        return next;
      });
    }, intervalMs);
    return () => clearInterval(id);
  }, [banners, count, intervalMs]);

  const items = useMemo(() => picks, [picks]);
  if (!items || items.length === 0) return null;

  if (variant === "landing") {
    return (
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${className}`} data-testid={testid}>
        {items.map((b) => (
          <Link
            key={b.id}
            to={b.link || "/outlet"}
            className="relative overflow-hidden rounded-sm group block hover-lift"
            data-testid={`${testid}-item-${b.id}`}
          >
            <img src={bannerImgSrc(b)} alt={b.title} className="w-full h-[260px] object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
            <div className="absolute bottom-0 left-0 p-6 text-cream">
              <div className="text-[11px] tracking-[0.3em] uppercase text-cream/70">{KIND_LABELS[b.kind] || "Novità"}</div>
              <div className="mt-1 font-display text-2xl">{b.title}</div>
              {b.subtitle && <div className="text-sm text-cream/80 mt-1">{b.subtitle}</div>}
            </div>
          </Link>
        ))}
      </div>
    );
  }

  // dashboard variant (default)
  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${className}`} data-testid={testid}>
      {items.map((b) => (
        <Link
          key={b.id}
          to={b.link || "/outlet"}
          className="relative overflow-hidden rounded-sm group block"
          data-testid={`${testid}-item-${b.id}`}
        >
          <img src={bannerImgSrc(b)} alt={b.title} className="w-full h-[220px] object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/20 to-transparent" />
          <div className="absolute bottom-0 left-0 p-6 text-cream">
            <div className="text-[10px] tracking-[0.3em] uppercase text-cream/70">{KIND_LABELS[b.kind] || "Novità"}</div>
            <div className="mt-1 font-display text-xl">{b.title}</div>
            {b.subtitle && <div className="text-sm text-cream/80">{b.subtitle}</div>}
          </div>
        </Link>
      ))}
    </div>
  );
}
